/**
 * API Error Handler Utility
 * Provides consistent error handling for PocketBase API errors
 * Returns user-friendly error messages and logs errors appropriately
 * Integrates with Sentry for production error tracking
 */

import * as Sentry from '@sentry/react'
import { logger } from './logger'

/**
 * Reports error to Sentry with context
 */
function reportToSentry(error: any, context?: string, extra?: Record<string, any>): void {
  // Only report if Sentry is initialized (DSN is set)
  if (!import.meta.env.VITE_SENTRY_DSN) return

  Sentry.withScope((scope) => {
    scope.setTag('error_type', 'api_error')
    if (context) {
      scope.setTag('context', context)
    }
    if (extra) {
      scope.setContext('api_details', extra)
    }
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)))
  })
}

export interface ApiError {
  message: string
  status?: number
  fieldErrors?: Record<string, string>
  originalError?: any
}

/**
 * Handles API errors and returns user-friendly error messages
 * @param error - The error object from PocketBase or other API calls
 * @param context - Optional context string for logging (e.g., "Client creation", "Prospect update")
 * @returns ApiError object with user-friendly message
 */
/**
 * The parsed PocketBase error body, or null if this is not one.
 *
 * The SDK's ClientResponseError sets `.response` to the body itself —
 * `{ code, message, data: { field: { code, message } } }` — and aliases `.data`
 * to the same object. This used to read `error.response.data`, which is the
 * FIELD-ERROR MAP, and then look for `.data` and `.message` on that. Neither
 * exists there, so every PocketBase validation failure fell through to the
 * generic branch: the user was told "Something went wrong" while the response
 * had been naming the exact field and reason all along.
 *
 * Axios-shaped errors put the body one level deeper, so both are accepted.
 */
function pocketBaseBody(error: any): any | null {
  const response = error?.response
  if (!response || typeof response !== 'object') return null
  // PocketBase: the body IS the response.
  if ('message' in response || 'code' in response) return response
  // Axios: the body sits under .data.
  if (response.data && typeof response.data === 'object') return response.data
  return null
}

export function handleApiError(error: any, context?: string): ApiError {
  const contextPrefix = context ? `${context}: ` : ''
  
  // Handle auto-cancellation (status 0) - this is expected when components unmount
  const status = error?.status || error?.response?.status || 0
  if (status === 0 || error?.message?.includes('autocancelled') || error?.message?.includes('auto-cancelled')) {
    // Auto-cancellation is not an error - return empty error that won't be logged
    // Don't log this as it's expected behavior
    return {
      message: '',
      status: 0,
      originalError: error
    }
  }
  
  // Handle PocketBase-specific errors
  const pbError = pocketBaseBody(error)
  if (pbError) {
    const status = error.status || error.response?.status || 500
    
    // Extract field-specific validation errors.
    // Keys are required, not just an object: PocketBase sends `data: {}` on
    // errors that are not field validation at all, and `{}` is truthy. Taking
    // this branch on an empty map told the user "Validation failed. Please
    // check your input." when nothing about their input was wrong and the body
    // carried the actual reason.
    if (pbError.data && typeof pbError.data === 'object' && Object.keys(pbError.data).length > 0) {
      const fieldErrors: Record<string, string> = {}
      const errorMessages: string[] = []
      
      Object.keys(pbError.data).forEach(field => {
        const fieldError = pbError.data[field]
        const errorMessage = fieldError?.message || JSON.stringify(fieldError)
        fieldErrors[field] = errorMessage
        errorMessages.push(`${field}: ${errorMessage}`)
      })
      
      const userMessage = errorMessages.length > 0
        ? `Validation failed: ${errorMessages.join(', ')}`
        : 'Validation failed. Please check your input.'
      
      // Log error
      logger.error(`${contextPrefix}${userMessage}`, { status, fieldErrors })

      // Report to Sentry in production
      reportToSentry(error, context, { status, fieldErrors, pbError })

      // Log detailed error in development
      if (import.meta.env.DEV) {
        logger.debug('PocketBase error details:', pbError)
      }

      return {
        message: userMessage,
        status,
        fieldErrors,
        originalError: error
      }
    }
    
    // Handle general PocketBase error messages
    if (pbError.message) {
      const userMessage = formatErrorMessage(pbError.message, status)
      logger.error(`${contextPrefix}${userMessage}`, { status })

      // Report to Sentry
      reportToSentry(error, context, { status, pbMessage: pbError.message })

      if (import.meta.env.DEV) {
        logger.debug('PocketBase error:', pbError)
      }

      return {
        message: userMessage,
        status,
        originalError: error
      }
    }
  }
  
  // Handle direct error objects with status
  if (error?.status && error?.message) {
    const userMessage = formatErrorMessage(error.message, error.status)
    logger.error(`${contextPrefix}${userMessage}`, { status: error.status })

    // Report to Sentry
    reportToSentry(error, context, { status: error.status })

    return {
      message: userMessage,
      status: error.status,
      originalError: error
    }
  }
  
  // Handle standard Error objects
  if (error instanceof Error) {
    // Check if it's an auto-cancellation error
    if (error.message?.includes('autocancelled') || error.message?.includes('auto-cancelled')) {
      return {
        message: '',
        status: 0,
        originalError: error
      }
    }
    const userMessage = formatErrorMessage(error.message)
    logger.error(`${contextPrefix}${userMessage}`)

    // Report to Sentry
    reportToSentry(error, context)

    return {
      message: userMessage,
      originalError: error
    }
  }
  
  // Handle string errors
  if (typeof error === 'string') {
    const userMessage = formatErrorMessage(error)
    logger.error(`${contextPrefix}${userMessage}`)

    // Report to Sentry
    reportToSentry(new Error(error), context)

    return {
      message: userMessage,
      originalError: error
    }
  }

  // Fallback for unknown error types
  const fallbackMessage = 'An unexpected error occurred. Please try again.'
  logger.error(`${contextPrefix}${fallbackMessage}`, error)

  // Report to Sentry
  reportToSentry(error, context, { type: 'unknown' })

  return {
    message: fallbackMessage,
    originalError: error
  }
}

/**
 * Formats error messages to be more user-friendly
 */
function formatErrorMessage(message: string, status?: number): string {
  if (!message) {
    return 'An error occurred. Please try again.'
  }
  
  // Handle common HTTP status codes
  if (status === 401) {
    return 'You are not authenticated. Please log in again.'
  }
  
  if (status === 403) {
    return 'You do not have permission to perform this action.'
  }
  
  if (status === 404) {
    return 'The requested resource was not found.'
  }

  if (status === 409) {
    return 'This record was modified by another user. Please refresh and try again.'
  }

  if (status === 400) {
    // Validation errors are handled separately, but this is a fallback
    return message.includes('Validation') ? message : 'Invalid request. Please check your input.'
  }
  
  if (status === 500 || status === 502 || status === 503) {
    return 'Server error. Please try again later.'
  }
  
  // Clean up PocketBase-specific error messages
  let cleanedMessage = message
  
  // Remove technical prefixes
  cleanedMessage = cleanedMessage.replace(/^Failed to fetch\.?\s*/i, '')
  cleanedMessage = cleanedMessage.replace(/^Network error:?\s*/i, '')
  
  // Capitalize first letter
  if (cleanedMessage.length > 0) {
    cleanedMessage = cleanedMessage.charAt(0).toUpperCase() + cleanedMessage.slice(1)
  }
  
  return cleanedMessage || 'An error occurred. Please try again.'
}

/**
 * Creates a user-friendly error message from an error object
 * This is a convenience function that wraps handleApiError and throws
 */
export function throwApiError(error: any, context?: string): never {
  const apiError = handleApiError(error, context)
  const errorToThrow = new Error(apiError.message)
  ;(errorToThrow as any).status = apiError.status
  ;(errorToThrow as any).fieldErrors = apiError.fieldErrors
  ;(errorToThrow as any).originalError = apiError.originalError
  throw errorToThrow
}

/**
 * Catch PocketBase 404 (not found) and return null. All other errors rethrow.
 * Use with getOne() and getFirstListItem() which throw 404 when no record matches.
 */
export function catch404<T>(promise: Promise<T>): Promise<T | null> {
  return promise.catch((err) => {
    if (err?.status === 404) return null
    throw err
  })
}
